import React, { useState, FC, useMemo, useCallback } from 'react';

interface ILink {
  id: number;
  fileName: string;
}

export interface IModalProps {
  type: 'success' | 'error' | 'confirm' | 'info' | 'loading' | 'form' | 'list';
  size?: 'sm' | 'lg' | 'xl';
  hideIcon?: boolean;
  heading?: string;
  content?: string | JSX.Element;
  links?: ILink[];
  onConfirm?: () => void;
  confirmLabel?: string;
  confirmBtnType?: 'danger' | 'info';
  onCancel?: () => void;
  onLinkClick?: (id: number) => void;
  cancelLabel?: string;
  onSuccess?: () => void;
  successButton?: JSX.Element;
  confirmButton?: JSX.Element;
}

interface IContextState {
  showModal: (props: IModalProps) => void;
  hideModal: () => void;
  isVisible: boolean;
  props: IModalProps;
}

export const ModalContext = React.createContext<IContextState>({
  showModal: () => {},
  hideModal: () => {},
  isVisible: false,
  props: {
    type: 'success',
    size: 'sm',
  },
});

interface IProps {
  children: React.ReactNode;
}

export const ModalProvider: FC<IProps> = ({ children }: IProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const initialValues: IModalProps = {
    type: 'success',
    size: 'sm',
  };
  const [props, setProps] = useState<IModalProps>(initialValues);

  const showModal = useCallback((modalProps: IModalProps) => {
    setIsVisible(true);
    setProps(modalProps);
  }, []);

  const hideModal = useCallback(() => {
    setIsVisible(false);
    setProps(initialValues);
  }, []);

  const modalValues = useMemo(() => {
    return {
      showModal: (modalProps: IModalProps) => showModal(modalProps),
      hideModal,
      isVisible,
      props,
    };
  }, [hideModal, isVisible, props, showModal]);

  return <ModalContext.Provider value={modalValues}>{children}</ModalContext.Provider>;
};

export function useModal(): IContextState {
  return React.useContext(ModalContext);
}

// Flags content rendered as the global modal's `content` prop, so nested
// components (e.g. file upload) know a global `showModal` error would hide
// the modal they're rendered in and should show errors inline instead.
export const IsInsideModalContext = React.createContext<boolean>(false);

export function useIsInsideModal(): boolean {
  return React.useContext(IsInsideModalContext);
}
